import React, { useEffect, useState } from 'react';
import styled from 'styled-components';
import useTheme from 'hooks/useTheme';
import AntButton from 'components/AntButton/AntButton';
import { Modal } from 'components';
import {
	formatMaterialValidationCaseLabel,
	formatMaterialValidationValue,
} from './materialValidationPresentation';

const statusLabels = {
	PENDING_REVIEW: 'Pendiente',
	APPROVED: 'Aprobada',
	RETURNED_FOR_CORRECTION: 'Devuelta para corregir',
	SUPERSEDED: 'Reemplazada',
	CANCELED: 'Cancelada',
};

const List = styled.ol`
	display: grid;
	gap: 10px;
	list-style: none;
	margin: 0;
	padding: 8px 0 0;
`;

const Queue = styled.section`
	display: grid;
	gap: 6px;
`;

const QueueTitle = styled.strong`
	font-size: 12px;
`;

const QueueList = styled.ol`
	display: grid;
	gap: 6px;
	list-style: none;
	margin: 0;
	padding: 0;
`;

const QueueItem = styled.li`
	border: 1px solid ${({ $border }) => $border};
	border-radius: 6px;
	color: ${({ $text }) => $text};
	font-size: 12px;
	padding: 8px;
`;

const Item = styled.li`
	background: ${({ $surface }) => $surface};
	border: 1px solid ${({ $border }) => $border};
	border-radius: 8px;
	color: ${({ $text }) => $text};
	display: grid;
	font-size: 13px;
	gap: 4px;
	padding: 12px;
`;

const Meta = styled.span`
	color: ${({ $color }) => $color};
	font-size: 12px;
`;

const ResolutionList = styled.ul`
	list-style: none;
	margin: 6px 0 0;
	padding: 0;

	> li + li {
		margin-top: 8px;
	}
`;

const ResolutionItem = styled.li`
	border-left: 3px solid ${({ $accent }) => $accent};
	padding-left: 8px;

	> * {
		display: block;
	}

	> * + * {
		margin-top: 3px;
	}
`;

const strategyLabels = {
	PAY_EXPECTED: 'Pagar esperado',
	PAY_REPORTED: 'Pagar reportado/calculado Synapse',
	PAY_ADJUSTED: 'Pagar cantidad ajustada',
};

const applicationModeLabels = {
	MUTABLE_REPRICE: 'Recálculo aplicado',
	ADVISORY_ONLY: 'Solo referencia; pago cerrado',
};

const getStrategyLabel = selection => {
	if (selection.selectionSource === 'SYSTEM_MATCH')
		return 'Coincidencia automática';
	return strategyLabels[selection.strategy] || selection.strategy;
};

const formatDate = value => {
	if (!value) return undefined;
	const date = new Date(value);
	if (Number.isNaN(date.getTime())) return undefined;
	return new Intl.DateTimeFormat('es-CR', {
		dateStyle: 'medium',
		timeStyle: 'short',
	}).format(date);
};

export default function MaterialValidationHistory({
	currentCase,
	previousCases = [],
}) {
	const { theme } = useTheme();
	const [visible, setVisible] = useState(false);
	const queuedCases = previousCases.filter(
		materialCase => materialCase.status === 'PENDING_REVIEW',
	);
	const previousHistoricalCases = previousCases.filter(
		materialCase => materialCase.status !== 'PENDING_REVIEW',
	);
	const currentTerminalCase =
		currentCase?.status !== 'PENDING_REVIEW' && currentCase?.latestDecision
			? currentCase
			: undefined;
	const historicalCases = currentTerminalCase
		? [
				currentTerminalCase,
				...previousHistoricalCases.filter(
					materialCase =>
						String(materialCase.id) !== String(currentTerminalCase.id),
				),
			]
		: previousHistoricalCases;
	const canView = historicalCases.length > 0;

	useEffect(() => setVisible(false), [currentCase, previousCases]);

	if (!canView && queuedCases.length === 0) return null;

	return (
		<>
			{queuedCases.length > 0 && (
				<Queue aria-label='Casos pendientes en cola'>
					<QueueTitle>Pendientes en cola ({queuedCases.length})</QueueTitle>
					<QueueList>
						{queuedCases.map(materialCase => (
							<QueueItem
								key={materialCase.id}
								$border={theme._borderColor}
								$text={theme._highEmphasisColor}
							>
								{formatMaterialValidationCaseLabel(materialCase)}
							</QueueItem>
						))}
					</QueueList>
				</Queue>
			)}
			{canView && (
				<AntButton
					size='small'
					onClick={() => setVisible(true)}
					aria-label={`Ver historial de validación de materiales (${historicalCases.length})`}
				>
					Ver historial ({historicalCases.length})
				</AntButton>
			)}
			<Modal
				visible={visible}
				title='Historial de validación de materiales'
				aria-label='Historial de validación de materiales'
				footer={null}
				destroyOnClose
				onCancel={() => setVisible(false)}
			>
				<List aria-label='Casos anteriores de validación de materiales'>
					{historicalCases.map(materialCase => {
						const decision = materialCase.latestDecision;
						const pricingSelections =
							decision?.pricingResolution?.selections || [];
						const actor =
							decision?.actorId === null || decision?.actorId === undefined
								? undefined
								: `Usuario ${decision.actorId}`;
						const decidedAt = formatDate(decision?.createdAt);
						return (
							<Item
								key={materialCase.id}
								$surface={theme._secondaryBackgroundColor}
								$border={theme._borderColor}
								$text={theme._highEmphasisColor}
							>
								<strong>
									{formatMaterialValidationCaseLabel(materialCase)} ·{' '}
									{statusLabels[materialCase.status] || 'Estado no verificable'}
								</strong>
								{(actor || decidedAt) && (
									<Meta $color={theme._mediumEmphasisColor}>
										{actor && <>Decidido por {actor}</>}
										{actor && decidedAt && ' · '}
										{decidedAt}
									</Meta>
								)}
								{decision?.reasonCode && (
									<span>Código: {decision.reasonCode}</span>
								)}
								{decision?.comment && (
									<span>Comentario: {decision.comment}</span>
								)}
								{decision?.applicationMode && (
									<span>
										Aplicación financiera:{' '}
										{applicationModeLabels[decision.applicationMode] ||
											decision.applicationMode}
									</span>
								)}
								{pricingSelections.length > 0 && (
									<section aria-label='Resolución de cantidades pagadas'>
										<strong>Resolución de pago por material</strong>
										<ResolutionList>
											{pricingSelections.map(selection => (
												<ResolutionItem
													key={selection.metricKey}
													$accent={theme._secondaryColor || theme._borderColor}
												>
													<strong>
														{selection.material?.label ||
															selection.material?.code ||
															selection.metricKey}
													</strong>
													<span>{getStrategyLabel(selection)}</span>
													<span>
														Esperado:{' '}
														{formatMaterialValidationValue(
															selection.expectedValue,
															selection.unit,
														)}{' '}
														· Reportado:{' '}
														{formatMaterialValidationValue(
															selection.reportedValue,
															selection.unit,
														)}
													</span>
													<span>
														Cantidad resuelta:{' '}
														{formatMaterialValidationValue(
															selection.resolvedValue,
															selection.unit,
														)}
													</span>
													{selection.justification && (
														<span>
															Justificación: {selection.justification}
														</span>
													)}
												</ResolutionItem>
											))}
										</ResolutionList>
									</section>
								)}
							</Item>
						);
					})}
				</List>
			</Modal>
		</>
	);
}
